import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import React from "react";
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Stack,
  Link,
  Divider,
  IconButton,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchNotifications } from "@modules/public-comments/redux/thunk/notificationsThunk";
import type { AppDispatch } from "@store/store";

import TranslateIcon from "@mui/icons-material/Translate";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";

import commonlogin from "../../assets/svg/emblem-commonlogin.svg";
import SeeAllNotificationsModal from "@modules/login/components/SeeAllNotificationsModal";
import { openFileBySignedUrl } from "../../utils/getViewSignedUrlHandler";
import { getFileROWSignedURLThunk } from "@modules/stepper/redux/thunk/documentThunk";
// import SeeAllNotificationsModal from "@modules/nocApplications/src/components/slider/SeeAllNotificationsModal";

const marquee = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-100%); }
`;

const CommonLoginHeader: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const isOrganisationPage = location.pathname === "/organisation";
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState<any[]>([]);
  const [currentNotifIndex, setCurrentNotifIndex] = React.useState(0);
  const SCROLL_DURATION = 40;

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleMarqueeIteration = () => {
    if (notifications.length > 1) {
      setCurrentNotifIndex((prev) => (prev + 1) % notifications.length);
    }
  };
  const [docsAnchorEl, setDocsAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const [docsAnchorElMobile, setDocsAnchorElMobile] =
    React.useState<null | HTMLElement>(null);
  const [guidelinesAnchorEl, setGuidelinesAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [notifOpen, setNotifOpen] = React.useState(false);

  // Refs to anchor menus on hover
  const docsBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const docsBtnRefMobile = React.useRef<HTMLButtonElement | null>(null);
  const guidelinesBtnRef = React.useRef<HTMLButtonElement | null>(null);

  const openDocs = (e: React.MouseEvent<HTMLButtonElement>) =>
    setDocsAnchorEl(e.currentTarget);
  const closeDocs = () => setDocsAnchorEl(null);

  const openDocsMobile = (e: React.MouseEvent<HTMLButtonElement>) =>
    setDocsAnchorElMobile(e.currentTarget);
  const closeDocsMobile = () => setDocsAnchorElMobile(null);

  const openGuidelines = (e: React.MouseEvent<HTMLButtonElement>) =>
    setGuidelinesAnchorEl(e.currentTarget);
  const closeGuidelines = () => setGuidelinesAnchorEl(null);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const action = await dispatch(fetchNotifications() as any);
        const payload = (action as any)?.payload;
        const arr = Array.isArray(payload) ? payload : [];
        if (mounted) setNotifications(arr);
      } catch {
        if (mounted) setNotifications([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [dispatch]);

  const handleOpenUserManual = (documentName: string) => {
    openFileBySignedUrl({
      document_name: documentName,
      dispatch,
      getSignedUrlThunk: getFileROWSignedURLThunk,
    });
  };

  React.useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://translation-plugin.bhashini.co.in/v3/website_translation_utility.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <>
      {/* Top purple bar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{ bgcolor: "#3a1a76", height: 32, justifyContent: "center" }}
      >
        <Toolbar
          sx={{ minHeight: 44, pl: { xs: 1, md: 7 }, pr: { xs: 2, md: 4 } }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            <img
              src={require("@assets/images/flag.jpg")}
              alt="India Flag"
              style={{ width: 33, height: 20, borderRadius: 2 }}
            />
            <Typography
              color="#fff"
              sx={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}
            >
              <Link
                component="a"
                href="https://india.gov.in/"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="#fff"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  "&:hover": { color: "#fff" },
                }}
                aria-label="Open Government of India website"
              >
                Government of India
                <OpenInNewOutlinedIcon
                  sx={{ fontSize: 16, color: "#cbb8ff" }}
                />
              </Link>
            </Typography>
          </Stack>
          <div className="flex mr-4 items-center gap-1">
            <span style={{ fontSize: 12 }} className="">
              Select Language
            </span>
            <div className="bhashini-plugin-container"></div>
          </div>
        </Toolbar>
      </AppBar>

      {/* White brand band */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #eee" }}>
        <Container
          maxWidth="xl"
          disableGutters
          sx={{
            px: { xs: 1, md: 5 },
            py: { xs: 2, md: 2 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "nowrap",
            gap: 3,
          }}
        >
          <Stack
            direction="row"
            spacing={2.5}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <Link
              component="a"
              href="/"
              rel="noopener noreferrer"
              sx={{ display: "inline-block" }}
            >
              <Box sx={{ textAlign: "left" }}>
                <img
                  src={require("@assets/images/logo/RajmargPraveshLogo.png")}
                  alt="MoRTH Logo"
                  className="morth-logo"
                />
              </Box>
            </Link>
          </Stack>

          {/* Hamburger for mobile */}
          <Box
            sx={{ display: { xs: "flex", md: "none" }, alignItems: "center" }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ ml: 1 }}
            >
              <MenuIcon sx={{ color: "#3a1a76", fontSize: 32 }} />
            </IconButton>
          </Box>

          {/* Desktop nav links */}
          {!isOrganisationPage && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                flexWrap: "nowrap",
                whiteSpace: "nowrap",
                flexShrink: 0,
                display: { xs: "none", md: "flex" },
              }}
            >
              <IconButton
                edge="start"
                size="small"
                color="inherit"
                disableRipple
                disableFocusRipple
                sx={{
                  color: "text.primary",
                  "&:hover": { bgcolor: "transparent" },
                }}
                component={RouterLink}
                to="/"
                aria-label="Home"
              >
                <HomeRoundedIcon />
              </IconButton>
              <Button
                component={RouterLink}
                to="/public-comments"
                color="inherit"
                disableRipple
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#212121",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                  gap: 1,
                  textTransform: "none",
                  "&:hover": { bgcolor: "transparent" },
                  textDecorationColor: "currentColor",
                  "&&:hover": {
                    color: "#212121",
                    textDecoration: "underline",
                    textDecorationColor: "currentColor",
                    backgroundColor: "transparent",
                  },
                  "&&:focus, &&:active": {
                    color: "#212121",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Invitation of Public Comments
              </Button>
              <Box onMouseEnter={() => setDocsAnchorEl(docsBtnRef.current)}>
                <Button
                  ref={docsBtnRef}
                  onClick={() => setDocsAnchorEl(docsBtnRef.current)}
                  endIcon={
                    docsAnchorEl ? (
                      <KeyboardArrowUpRoundedIcon />
                    ) : (
                      <KeyboardArrowDownRoundedIcon />
                    )
                  }
                  color="inherit"
                  disableRipple
                  sx={{
                    color: "#212121",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "1.1rem",
                    "&:hover": { bgcolor: "transparent" },
                  }}
                  aria-controls={docsAnchorEl ? "docs-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={!!docsAnchorEl}
                >
                  Required Documents
                </Button>
              </Box>
              <Button
                component={RouterLink}
                to="/guidelines"
                color="inherit"
                disableRipple
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#212121",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                  gap: 1,
                  textTransform: "none",
                  "&:hover": { bgcolor: "transparent" },
                  textDecorationColor: "currentColor",
                  "&&:hover": {
                    color: "#212121",
                    textDecoration: "underline",
                    textDecorationColor: "currentColor",
                    backgroundColor: "transparent",
                  },
                  "&&:focus, &&:active": {
                    color: "#212121",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Guidelines
              </Button>
              <Button
                component="a"
                onClick={() =>
                  handleOpenUserManual(
                    "1792655086851-jlx2ty3dfhm-user-manual.pdf",
                  )
                }
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                disableRipple
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#212121",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                  gap: 1,
                  textTransform: "none",
                  "&:hover": { bgcolor: "transparent" },
                  textDecorationColor: "currentColor",
                  "&&:hover": {
                    color: "#212121",
                    textDecoration: "underline",
                    textDecorationColor: "currentColor",
                    backgroundColor: "transparent",
                  },
                  "&&:focus, &&:active": {
                    color: "#212121",
                    backgroundColor: "transparent",
                  },
                }}
              >
                User Manual
              </Button>
            </Stack>
          )}

          {/* Mobile Drawer for nav links */}
          <Drawer
            anchor="left"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: "70vw" },
            }}
          >
            <Box sx={{ width: 260, p: 2 }} role="presentation">
              <Link
                component="a"
                href="/"
                rel="noopener noreferrer"
                sx={{ display: "inline-block" }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <img
                    src={require("@assets/images/logo/RajmargPraveshLogo.png")}
                    alt="MoRTH Logo"
                    className="morth-logo"
                    style={{ marginLeft: 0 }}
                  />
                </Box>
              </Link>
              <Divider sx={{ my: 2 }} />
              <Button
                component={RouterLink}
                to="/public-comments"
                color="inherit"
                disableRipple
                sx={{
                  display: "block",
                  color: "#212121",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                  mb: 2,
                }}
                onClick={handleDrawerToggle}
              >
                Invitation of Public Comments
              </Button>
              <Button
                ref={docsBtnRefMobile}
                onClick={() => {
                  setDocsAnchorEl(docsBtnRefMobile.current);
                }}
                endIcon={
                  docsAnchorEl ? (
                    <KeyboardArrowUpRoundedIcon />
                  ) : (
                    <KeyboardArrowDownRoundedIcon />
                  )
                }
                color="inherit"
                disableRipple
                sx={{
                  display: "inline-flex",
                  color: "#212121",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                  mb: 2,
                  alignItems: "center",
                  "&:hover": { bgcolor: "transparent" },
                }}
                aria-controls={docsAnchorEl ? "docs-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={!!docsAnchorEl}
              >
                Required Documents
              </Button>
              <Button
                component={RouterLink}
                to="/guidelines"
                color="inherit"
                disableRipple
                sx={{
                  display: "block",
                  color: "#212121",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                  mb: 2,
                }}
                onClick={handleDrawerToggle}
              >
                Guidelines
              </Button>
              <Button
                component="a"
                href="/user-manual.pdf"
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                disableRipple
                sx={{
                  display: "block",
                  color: "#212121",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                  mb: 2,
                }}
                onClick={handleDrawerToggle}
              >
                User Manual
              </Button>
            </Box>
          </Drawer>
          <Menu
            id="docs-menu"
            anchorEl={docsAnchorEl}
            open={Boolean(docsAnchorEl)}
            onClose={closeDocs}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            disableScrollLock
          >
            <MenuItem
              component={RouterLink}
              to="/access-permission"
              onClick={() => {
                closeDocs();
                handleDrawerToggle();
              }}
            >
              Document List for Access Permission
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/right-of-way-permission"
              onClick={() => {
                closeDocs();
                handleDrawerToggle();
              }}
            >
              Document List for Right of Way Permission
            </MenuItem>
          </Menu>
        </Container>
      </Box>

      {/* Notification Marquee Section */}
      {!isOrganisationPage && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 2,
            fontWeight: 500,
            fontSize: 12,
            color: "#212121",
            bgcolor: "#fdf3e2",
            borderTop: "1px solid #f2e4cd",
            px: { xs: 1, md: 7 },
            py: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <NotificationsRoundedIcon sx={{ color: "#f0b905" }} />
            <Typography sx={{ fontWeight: 700 }}>Notification</Typography>
          </Stack>

          {/* Marquee container — dynamic notifications */}
          <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            {notifications.length > 0 ? (
              <Box
                key={currentNotifIndex}
                onAnimationIteration={handleMarqueeIteration}
                sx={{
                  display: "inline-block",
                  whiteSpace: "nowrap",
                  paddingLeft: "100%",
                  animation: `${marquee} ${SCROLL_DURATION}s linear infinite`,
                }}
                title={
                  notifications[currentNotifIndex]?.title ||
                  notifications[currentNotifIndex]?.notification_title ||
                  ""
                }
              >
                <Typography
                  component="span"
                  sx={{ fontSize: 12, fontWeight: 500 }}
                >
                  {notifications[currentNotifIndex]?.title ||
                    notifications[currentNotifIndex]?.notification_title ||
                    "Untitled Notification"}
                </Typography>
              </Box>
            ) : (
              <Typography
                component="span"
                sx={{ fontSize: 12, fontWeight: 500 }}
              >
                No notifications available.
              </Typography>
            )}
          </Box>

          <Link
            // onClick={() => setNotifOpen(true)}
            component="a"
            href="/notifications"
            underline="none"
            sx={{
              ml: "auto",
              fontWeight: 700,
              color: "#3A1C73",
              fontSize: 12,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            View
          </Link>
        </Box>
      )}
      <SeeAllNotificationsModal
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        notifications={notifications}
      />
    </>
  );
};

export default CommonLoginHeader;
